import { FC, useCallback, useState } from "react";
import { ISource } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client"
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import SpinnerOverlay from "../Common/SpinnerOverlay";
import { Body2, Button, makeStyles, shorthands, Subtitle1, Title1, Title2, Title3, Tooltip } from "@fluentui/react-components";
import { Open24Filled } from '@fluentui/react-icons';
import { EllipsisText } from "../Common/EllipsisText";
import { useNotification } from "../../Contexts/NotificationContext";

export interface SourcePreviewProps {
    source: ISource;
    conversationId: string;
}

const useStyles = makeStyles({
    sourceContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('1rem')
    },
    headerContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
    },
    body: {
        whiteSpace: 'pre-line'
    }
});

// Component to display a preview of a source (e.g. for RAG tools)
export const SourcePreview: FC<SourcePreviewProps> = ({ source, conversationId }: SourcePreviewProps) => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(false);
    const { dispatchNotification } = useNotification();

    // Function to open the source in a new tab
    const openSource = () => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                // if the source is a website, open it in a new tab
                if (source.webSiteUrl) {
                    window.open(source.webSiteUrl, '_blank');
                }
                else {
                    setIsLoading(true);
                    const doc = await client.downloadDocument(conversationId, source.url!);
                    const blob = new Blob([doc.data], { type: doc.data.type });
                    const file = new File([blob], source.fileName!, { type: doc.data.type });
                    const url = URL.createObjectURL(file);
                    // currently, pageNumber field is not correctly represented on the plugin, so we are not using it
                    // window.open(source.pageNumber && source.pageNumber !== "0" ? url + "#page=" + source.pageNumber : url, '_blank');
                    window.open(url, '_blank');
                }
            } catch (e: unknown) {
                dispatchNotification('error.api.get.document', 'error');
            } finally {
                setIsLoading(false);
            }
        });
    };

    // Render the source preview. It includes the source title, a button to open the source, and the source content in plain text
    return (
        <div className={classes.sourceContainer}>
            <div className={classes.headerContainer}>
                <Subtitle1>{source.webSiteUrl ? `${source.webSiteUrl}` : `${source.title}`}</Subtitle1>
                <SpinnerOverlay isLoading={isLoading} children={
                    <Tooltip content={source.webSiteUrl ? `${source.webSiteUrl}` : `${source.title}`} relationship="label">
                        <Button icon={<Open24Filled />} size='small' onClick={openSource}>
                            {source.webSiteUrl ? 'Open website' : 'Open document'}
                        </Button>
                    </Tooltip>
                } />
            </div>
            <Subtitle1>Paragraph content</Subtitle1>
            <Body2 className={classes.body} italic>"{source.chunk}"</Body2>
        </div>
    );
}